import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  getBillingCycleLabel,
  getPaymentPastDueGracePeriodEnd,
  getPaymentPastDueStatus,
  getSeatLimitGracePeriodEnd,
  getSeatLimitStatus,
  isLimitedPlan,
  PLAN_DETAILS,
} from '@/common/utils/license';
import { isUserAdmin } from '@/common/utils/organisation';
import BillingDetails from '@/dashboard/SettingsView/Billing/BillingDetails';
import BillingOverview from '@/dashboard/SettingsView/Billing/BillingOverview';
import {
  SectionContainer,
  SectionContent,
  SectionHeader,
} from '@/dashboard/SettingsView/Billing/BillingPageStyles';
import PaymentSuccessDialogue from '@/dashboard/SettingsView/Billing/PaymentSuccessDialogue';
import UsageDetails from '@/dashboard/SettingsView/Billing/UsageDetails';
import {
  SettingsContentContainer,
  SettingsHeaderContainer,
  SettingsHeaderText,
  SettingsPageContainer,
} from '@/dashboard/SettingsView/SettingsPageStyles';
import PlanDetails from '@/dashboard/Upgrade/PlanDetails';
import { WarningIcon } from '@phosphor-icons/react';
import { Alert, AlertDescription, AlertTitle } from '@shadcn/components/alert';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

export default function BillingView() {
  const userId = useAppSelector(selectUserId);

  const { organisation } = useOrganisation();

  const seatCount = organisation?.license.seatCount ?? 0;
  const licensePlan = organisation?.license.plan ?? 'trial';
  const billingCycle = organisation?.license.billingCycle ?? '1year';
  const billingInfo = organisation?.license.billingInfo ?? null;
  const currency = organisation?.license.currency ?? 'USD';
  const coupon = organisation?.license.coupon ?? null;
  const planDetails = PLAN_DETAILS[licensePlan];
  const studentCount = organisation?.studentCount ?? 0;

  const [paymentSuccessDialogueOpen, setPaymentSuccessDialogueOpen] =
    useState(false);

  useEffect(() => {
    const showPaymentSuccessDialogue = sessionStorage.getItem(
      'showPaymentSuccessDialogue',
    );
    if (showPaymentSuccessDialogue) {
      // Show the payment success dialogue
      setPaymentSuccessDialogueOpen(true);
      sessionStorage.removeItem('showPaymentSuccessDialogue');
    }
  }, []);

  const paymentPastDueStatus = useMemo(() => {
    return getPaymentPastDueStatus(organisation?.license);
  }, [organisation]);

  const seatLimitStatus = useMemo(() => {
    return getSeatLimitStatus(
      organisation?.license,
      organisation?.studentCount,
    );
  }, [organisation]);

  return (
    <SettingsPageContainer>
      <SettingsHeaderContainer>
        <SettingsHeaderText>
          <h2>Billing & Subscription</h2>
          <div>View and manage your Spellcaster subscription</div>
        </SettingsHeaderText>
      </SettingsHeaderContainer>
      <SettingsContentContainer>
        {(paymentPastDueStatus === 'past-due' ||
          paymentPastDueStatus === 'grace-ended') && (
          <Alert variant="destructive">
            <WarningIcon size={16} className="ml-1" />
            <AlertTitle>
              There was a problem processing your last payment.{' '}
            </AlertTitle>
            <AlertDescription>
              Please update your payment method before{' '}
              {dayjs(
                getPaymentPastDueGracePeriodEnd(
                  organisation?.license.becamePastDueAt ?? null,
                ),
              ).format('DD MMM YYYY')}{' '}
              to avoid losing access to your account.
            </AlertDescription>
          </Alert>
        )}

        {(seatLimitStatus === 'over-limit' ||
          seatLimitStatus === 'grace-ended') && (
          <Alert variant="warning">
            <WarningIcon size={18} className="ml-0.5" />
            <AlertTitle className="text-sm pr-2">
              You're over your learner limit
            </AlertTitle>
            <AlertDescription className="flex flex-col">
              <span>
                Your current plan includes {seatCount} learners. You'll need to
                remove {studentCount - seatCount} learner
                {studentCount - seatCount === 1 ? '' : 's'} or upgrade your plan
                {organisation?.license.seatLimitExceededAt
                  ? ` by ${dayjs(
                      getSeatLimitGracePeriodEnd(
                        organisation.license.seatLimitExceededAt,
                      ),
                    ).format('DD/MM/YYYY')}`
                  : ''}{' '}
                to retain access to Spellcaster.
              </span>
            </AlertDescription>
          </Alert>
        )}

        <BillingOverview />
        <UsageDetails />

        {isLimitedPlan(licensePlan) && isUserAdmin(organisation, userId) ? (
          <>
            <SectionContainer>
              <SectionHeader>
                <span className="font-medium text-lg">Billing Details</span>
                <span className="text-sm text-muted-foreground">
                  Manage your payment method and billing information
                </span>
              </SectionHeader>

              <SectionContent className="bg-muted/50 rounded-md p-4">
                <BillingDetails billingInfo={billingInfo} loading={false} />
              </SectionContent>
            </SectionContainer>

            <SectionContainer>
              <SectionHeader>
                <span className="font-medium text-lg">Plan Details</span>
                <span className="text-sm text-muted-foreground">
                  You are on the{' '}
                  <span className="font-medium text-foreground px-1 py-0.5 bg-muted rounded-md">
                    {planDetails.name}
                  </span>{' '}
                  plan billed{' '}
                  <span className="font-medium text-foreground px-1 py-0.5 bg-muted rounded-md">
                    {getBillingCycleLabel(billingCycle)}
                  </span>
                </span>
              </SectionHeader>

              <SectionContent>
                <PlanDetails
                  plan={licensePlan}
                  seatCount={seatCount}
                  billingCycle={billingCycle}
                  currency={currency}
                  coupon={coupon}
                  hideHeader
                />
              </SectionContent>
            </SectionContainer>
          </>
        ) : null}
      </SettingsContentContainer>

      <PaymentSuccessDialogue
        isOpen={paymentSuccessDialogueOpen}
        onClose={() => setPaymentSuccessDialogueOpen(false)}
      />
    </SettingsPageContainer>
  );
}
